import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  BIRTHDAY_WISHES,
  BIRTHDAY_WISH_DELETE,
  BIRTHDAY_WISH_SAVE,
} from "../../service/apiUrl";
import { postApiCall } from "../../service/PostApiCall";
import DataTable from "../../components/table/DataTable";
import CustomModal from "../../components/CustomModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import LoadingButton from "../../components/LoadingButton";
import ActionTooltip from "../../components/ActionTooltip";
import Utils from "../../utility/utility";

/* The pool birthday wishes are picked from (backend/models/BirthdayWish.php).
   On someone's birthday one active wish is picked for them and kept for the
   day: they see it in the strip at the top of every page, and the dashboards
   quote it under "Today is <name>'s Birthday!". A paused wish is never picked,
   but whoever already has it today keeps it. `{name}` becomes the person's
   first name. The pool is a few dozen rows, so the table is client-side. */

const MAX_LENGTH = 1000;
const ACTIVE = "1";
const PAUSED = "2";
const PREVIEW_NAME = "Priya";

const emptyDraft = { id: null, message: "", status: ACTIVE };

// `{name}` shown as a chip, so it reads as a slot rather than a typo
const WishText = ({ text }) =>
  String(text ?? "")
    .split(/(\{name\})/g)
    .map((part, i) =>
      part === "{name}" ? (
        <span key={i} className="badge bg-secondary-lt mx-1">name</span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );

const BirthdayWishes = (props) => {
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    if (!access_token) return;
    postApiCall({}, access_token, BIRTHDAY_WISHES)
      .then((response) => setRows(Array.isArray(response) ? response : []))
      .catch((e) => console.error("BIRTHDAY_WISHES", e))
      .finally(() => setLoaded(true));
  }, [access_token, refresh]);

  const openDraft = (wish) => {
    setError("");
    setDraft(wish ? { id: wish.id, message: wish.message, status: wish.status } : emptyDraft);
  };

  const save = async () => {
    const message = draft.message.trim();
    if (!message) {
      setError("Write the wish first.");
      return false;
    }
    try {
      await postApiCall(
        { id: draft.id, message, status: Number(draft.status), showSuccessToast: true },
        access_token,
        BIRTHDAY_WISH_SAVE
      );
    } catch (e) {
      console.error("BIRTHDAY_WISH_SAVE", e);
      setError(e?.data?.message || "The wish could not be saved.");
      throw e;
    }
    setDraft(null);
    setRefresh((n) => n + 1);
  };

  const toggleStatus = (wish) =>
    postApiCall(
      { id: wish.id, message: wish.message, status: wish.status === ACTIVE ? 2 : 1 },
      access_token,
      BIRTHDAY_WISH_SAVE
    )
      .then(() => setRefresh((n) => n + 1))
      .catch((e) => {
        console.error("BIRTHDAY_WISH_STATUS", e);
        Utils.toast("error", e?.data?.message || "The wish could not be updated.");
      });

  const remove = async () => {
    try {
      await postApiCall({ id: pendingDelete.id, showSuccessToast: true }, access_token, BIRTHDAY_WISH_DELETE);
    } catch (e) {
      console.error("BIRTHDAY_WISH_DELETE", e);
      Utils.toast("error", e?.data?.message || "The wish could not be deleted.");
      setRefresh((n) => n + 1);
      throw e;
    }
    setPendingDelete(null);
    setRefresh((n) => n + 1);
  };

  const activeCount = rows.filter((row) => row.status === ACTIVE).length;

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => <span className="text-nowrap">{row.getDisplayIndex() + 1}</span>,
      },
      {
        accessorKey: "message",
        header: "Wish",
        enableHiding: false,
        cell: ({ row }) => (
          <span style={{ whiteSpace: "normal" }}>
            <WishText text={row.original.message} />
          </span>
        ),
      },
      {
        id: "status",
        accessorFn: (row) => (row.status === ACTIVE ? "Active" : "Paused"),
        header: "Status",
        cell: ({ getValue }) => (
          <span className={`badge ${getValue() === "Active" ? "bg-success-lt" : "bg-secondary-lt"}`}>
            {getValue()}
          </span>
        ),
      },
      {
        accessorKey: "times_used",
        header: "Given",
        cell: ({ row }) => (
          <span className="text-nowrap">
            {row.original.times_used
              ? `${row.original.times_used} birthday${row.original.times_used > 1 ? "s" : ""}`
              : "Not yet"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "112px", className: "dt-sticky-end" },
        cell: ({ row }) => (
          <div className="row-actions">
            <ActionTooltip label="Edit">
              <button
                type="button"
                className="button-popup"
                aria-label="Edit wish"
                onClick={() => openDraft(row.original)}
              >
                <i className="fa fa-pencil site-color" aria-hidden="true"></i>
              </button>
            </ActionTooltip>
            <ActionTooltip label={row.original.status === ACTIVE ? "Pause" : "Activate"}>
              <button
                type="button"
                className="button-popup"
                aria-label={row.original.status === ACTIVE ? "Pause wish" : "Activate wish"}
                onClick={() => toggleStatus(row.original)}
              >
                <i
                  className={`fa ${row.original.status === ACTIVE ? "fa-pause" : "fa-play"} site-color`}
                  aria-hidden="true"
                ></i>
              </button>
            </ActionTooltip>
            <ActionTooltip label="Delete">
              <button
                type="button"
                className="button-popup"
                aria-label="Delete wish"
                onClick={() => setPendingDelete(row.original)}
              >
                <i className="site-color fa fa-trash" aria-hidden="true"></i>
              </button>
            </ActionTooltip>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [access_token]
  );

  const preview = draft?.message.trim().replace(/\{name\}/g, PREVIEW_NAME);

  return (
    <div className={props?.data?.transtion ? "contents" : "content"}>
      <div className="content-top-spacer"></div>
      <div className="color-body">
        <div className="page-head">
          <div className="page-eyebrow">Workplace</div>
          <h1 className="page-title">Birthday Wishes</h1>
          <p className="page-sub">
            On someone's birthday one active wish is picked for them and kept
            all day. They see it at the top of every page, and the dashboards
            show it under their birthday banner.
            {loaded ? ` ${activeCount} of ${rows.length} active.` : ""}
          </p>
        </div>
        <div className="mt-4">
          <div className="card-input mt-3">
            <div className="col-md-12 mb-3">
              <div className="mt-3">
                <DataTable
                  columns={columns}
                  data={rows}
                  isLoading={!loaded}
                  getRowId={(row) => String(row.id)}
                  search={{ columns: ["message"], placeholder: "Search wishes..." }}
                  facets={[{ columnId: "status", label: "Status" }]}
                  actions={
                    <button type="button" className="dt-btn" onClick={() => openDraft(null)}>
                      <i className="fa fa-plus" aria-hidden="true"></i>
                      Add wish
                    </button>
                  }
                  pagination={{ pageSize: 25, pageSizeOptions: [10, 25, 50, 100] }}
                  emptyText="No wishes yet. Add one so birthdays have something to show."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        isShow={Boolean(draft)}
        onHide={() => setDraft(null)}
        heading={draft?.id ? "Edit wish" : "Add wish"}
        description="Write it to the person whose birthday it is. Type {name} where their first name should go."
        footer={
          <>
            <button
              type="button"
              className="app-dialog-btn app-dialog-btn-ghost"
              onClick={() => setDraft(null)}
            >
              Cancel
            </button>
            <LoadingButton
              className="app-dialog-btn app-dialog-btn-primary"
              onAction={save}
              successLabel="Saved"
              errorLabel="Try again"
            >
              {draft?.id ? "Save" : "Add wish"}
            </LoadingButton>
          </>
        }
      >
        {draft ? (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor="birthday-wish-message">Wish</label>
              <textarea
                id="birthday-wish-message"
                className="form-control"
                rows={4}
                maxLength={MAX_LENGTH}
                value={draft.message}
                onChange={(e) => {
                  setError("");
                  setDraft({ ...draft, message: e.target.value });
                }}
              />
              <div className="d-flex justify-content-between mt-1">
                <small className="text-danger">{error}</small>
                <small className="text-muted">
                  {draft.message.length}/{MAX_LENGTH}
                </small>
              </div>
            </div>
            <label className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={draft.status === ACTIVE}
                onChange={(e) => setDraft({ ...draft, status: e.target.checked ? ACTIVE : PAUSED })}
              />
              <span className="form-check-label">Active (can be picked for a birthday)</span>
            </label>
            {preview ? (
              <div className="mt-3">
                <div className="form-label">How {PREVIEW_NAME} would see it</div>
                <blockquote className="birthday-wish-quote">{preview}</blockquote>
              </div>
            ) : null}
          </>
        ) : null}
      </CustomModal>

      <ConfirmDialog
        isShow={Boolean(pendingDelete)}
        onHide={() => setPendingDelete(null)}
        title="Delete this wish?"
        description="It is removed from the pool. Anyone who has it today is given another one. This cannot be undone."
        confirmLabel="Delete"
        successLabel="Deleted"
        tone="danger"
        onConfirm={remove}
      />
    </div>
  );
};

export default BirthdayWishes;
